import {compact} from '@benchling/js-utils';
import isEqual from 'react-fast-compare';

import {InaccessibleInfo, isInaccessibleInfo} from 'auth/RequestAccess';
import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import {getStore} from 'redux-stores/Store';
import {
  HydratedFulfillment,
  HydratedFulfillmentWithPreparedSampleGroup,
} from 'requests/fulfillments/HydratedFulfillmentConstants';
import {getContainerIds} from 'requests/SampleGroupUtil';
import StorableStore from 'samples/StorableStore';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {prepareSampleGroups} from 'workflows/samples/prepareSampleGroupsForTable';

import {BaseRequestEntryTableProps, RequestEntryTableConnectorProps} from './RequestEntryTable';

/**
 * Exports a React component which fetches all of the required data for working with request fulfillments.
 */

// The retrieval is done using a data connector. The DC is exported only for testing. Use the component in
// application code.
export class RequestEntryTableConnector extends DataConnector<
  BaseRequestEntryTableProps & RequestFulfillmentsRetrieverProps,
  RequestEntryTableConnectorProps
> {
  override stores = [StorableStore];

  _getContainerIds(
    hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup | InaccessibleInfo>
  ): Array<string> {
    const sampleGroupsWithSchemas = [];
    for (const hydratedFulfillment of hydratedFulfillments) {
      if (hydratedFulfillment.sampleGroup) {
        sampleGroupsWithSchemas.push({
          sampleGroup: hydratedFulfillment.sampleGroup,
          sampleGroupSchema: hydratedFulfillment.requestSchema.sampleGroupSchema,
        });
      }
    }
    return getContainerIds(sampleGroupsWithSchemas);
  }

  enhanceProps(): RequestEntryTableConnectorProps {
    const state = getStore().getState();
    const {fulfillmentIdsByEntryId} = state.models.requests.fulfillments;
    if (fulfillmentIdsByEntryId == null) {
      // FULFILLMENTS_LOADED_FOR_ENTRY is dispatched when the entry is opened
      return this.fallBackToLoading();
    }

    const fulfillmentIds = fulfillmentIdsByEntryId[this.props.entryId] || [];

    const hydratedFulfillmentById = this.getOrFetchMultipleMaybeInaccessibleRequired(
      ManagedModelType.HYDRATED_FULFILLMENT_BY_ID,
      fulfillmentIds
    );

    // Bioentities may not have already been fetched into the redux store.
    const stageRunSamplesById = new WorkflowSelector(state.models.workflows).getStageRunSampleById();
    const bioentityIdsToFetch = compact(
      Object.values(hydratedFulfillmentById)
        .map((details: HydratedFulfillment) =>
          (details.sampleGroup?.tags ?? []).map((tag) => {
            if (tag.targetSamples != null) {
              return tag.targetSamples.map((sample) =>
                sample.id != null ? stageRunSamplesById[sample.id]?.fileId : undefined
              );
            } else if (tag.targetFiles != null) {
              return tag.targetFiles.map(({id}) => id);
            }
            return [];
          })
        )
        .flat(2)
    );
    this.getOrFetchMultipleRequired(ManagedModelType.BIOENTITY_SUMMARY, bioentityIdsToFetch);

    // batchesById and containersById are fetched as part of fetching HYDRATED_FULFILLMENT_BY_ID
    // when this.getOrFetchMultiple() is called. If we get here we assume that they are already fetched.
    const batchesById = this.storableStore().getBatches();
    const containersById = this.storableStore().getContainers();
    // It's possible that a container has been invalidated (i.e. if the container were moved). Check for null
    // containers and trigger a fetch for them.
    this.getOrFetchMultipleRequired(
      ManagedModelType.CONTAINER,
      Object.keys(containersById).filter((containerId) => containersById[containerId] == null)
    );

    const {ancestralStorablesById} = this.storableStore().getStorableSummaryExtras();

    const sampleGroupIds = Object.values(hydratedFulfillmentById)
      .map((details) => details.sampleGroup?.id)
      .filter((x) => x);

    const enhancedSampleGroupsById = prepareSampleGroups(state, sampleGroupIds, {batchesById});

    // Enhance sample groups in the hydratedFulfillments
    const hydratedFulfillments = Object.values(hydratedFulfillmentById).map(
      (details: HydratedFulfillment) => ({
        ...details,
        sampleGroup: details.sampleGroup ? enhancedSampleGroupsById[details.sampleGroup.id] : null,
      })
    );

    // Fetch the relevant storable summary extras
    const allContainerIds = this._getContainerIds(hydratedFulfillments);
    for (const containerId of allContainerIds) {
      StorableSummaryExtrasManager.requestFetch(containerId);
    }

    let areContainerLocationsLoading = false;
    for (const containerId of allContainerIds) {
      if (
        ancestralStorablesById?.[containerId] == null ||
        containersById[containerId] == null // True if a container was invalidated.
      ) {
        areContainerLocationsLoading = true;
        break;
      }
    }
    if (areContainerLocationsLoading) {
      this.fallBackToLoading();
    }
    return {
      fulfillmentIds,
      hydratedFulfillments,
      containersById,
      allContainerIds,
      ancestralStorablesById: ancestralStorablesById || null,
      areContainerLocationsLoading,
      areAnyFulfillmentsInaccessible: hydratedFulfillments.some((info) => isInaccessibleInfo(info)),
    };
  }
}

interface RequestFulfillmentsRetrieverProps {
  children: (props: RequestEntryTableConnectorProps) => JSX.Element | null;
}

function RawRequestFulfillmentsRetriever(
  props: BaseRequestEntryTableProps & RequestEntryTableConnectorProps & RequestFulfillmentsRetrieverProps
): JSX.Element | null {
  return props.children(props);
}

const RequestFulfillmentsRetriever = RequestEntryTableConnector.connect(RawRequestFulfillmentsRetriever, {
  throttleUpdates: true,
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});
export default RequestFulfillmentsRetriever;
